/**
 * Coordenação do discador entre as abas do mesmo navegador (mesma origem), por usuário.
 *
 * O Callix aceita UMA conexão por usuário. No modo SDK (SDK rodando no navegador) só uma aba
 * pode segurar a conexão: a "líder". As outras abas sabem que o usuário está conectado
 * (são "membros") e podem assumir a conexão ou sair de todas de uma vez. A decisão de usar
 * o discador (ou não, se o agente prefere o Callix web) é tomada uma vez e vale para todas.
 *
 * Peças:
 * - `DialerTabBus`: BroadcastChannel (ou `storage` como reserva) com ping/pong de presença,
 *   avisos de login/logout/queda/saída da líder/pedido de assumir.
 * - trava da líder em localStorage (`claimLeader`/`readLeader`), com batimento e prazo de
 *   validade, para duas abas não criarem sessão ao mesmo tempo.
 * - papel desta aba em sessionStorage (`saveTabRole`/`readTabRole`): sobrevive ao F5 e morre
 *   com a aba, então "F5 na aba líder" reconecta sozinho e "aba nova" pergunta.
 */

export type TabRole = 'leader' | 'member';

/** o que a tela de login mostra para um usuário conhecido (HTML ou último login) */
export interface LoginConfirmation {
  username: string;
  /** usuário veio do HTML: não dá para trocar */
  locked: boolean;
  /** modo SDK: outra aba deste navegador segura a conexão; oferece trazer para cá ou sair de todas */
  elsewhere?: boolean;
  /** aviso neutro (ex.: "você saiu do discador em outra aba") */
  note?: string | null;
}

export type TabMessage =
  | { type: 'ping'; from: string }
  | { type: 'pong'; from: string; role: TabRole | null; authenticated: boolean }
  /** uma aba entrou (virou líder no modo SDK, ou entrou pelo motor) */
  | { type: 'login'; from: string }
  /** o usuário clicou em Sair em alguma aba: todas saem */
  | { type: 'logout'; from: string }
  /** o Callix derrubou a sessão da líder (login em outro lugar etc.): ninguém assume */
  | { type: 'dropped'; from: string; originator: string | null }
  /** a líder está fechando ou recarregando: membros podem assumir depois do prazo */
  | { type: 'leaving'; from: string }
  /** um membro pede a conexão para si; a líder solta */
  | { type: 'takeover'; from: string }
  /** uma aba assumiu a liderança (cancela outras tentativas) */
  | { type: 'claim'; from: string };

export interface PongInfo {
  tabId: string;
  role: TabRole | null;
  authenticated: boolean;
}

export interface TabStatus {
  role: TabRole | null;
  authenticated: boolean;
}

/** o que o bus precisa de um canal: o subconjunto do BroadcastChannel que usamos */
export interface ChannelLike {
  postMessage(message: unknown): void;
  close(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onmessage: ((event: any) => void) | null;
}

/** mensagem a postar: o bus preenche `from` */
export type OutgoingTabMessage = TabMessage extends infer M ? (M extends TabMessage ? Omit<M, 'from'> : never) : never;

export interface DialerTabBusOptions {
  username: string;
  /** quem responde ao ping (papel e autenticação desta aba) */
  status: () => TabStatus;
  tabId?: string;
  channelFactory?: (name: string) => ChannelLike | null;
  storage?: Storage | null;
}

/** quanto esperar as outras abas responderem ao ping (uma aba ocupada com o SDK demora) */
export const PROBE_TIMEOUT_MS = 600;
/** quanto tempo a líder tem para voltar de um F5 antes de um membro assumir */
export const TAKEOVER_GRACE_MS = 3_000;
/** sorteio somado ao prazo para dois membros não assumirem juntos */
export const TAKEOVER_JITTER_MS = 600;
export const LEADER_HEARTBEAT_MS = 3_000;
/** trava sem batimento há mais que isto é de uma aba que morreu */
export const LEADER_STALE_MS = 9_000;

export function channelName(username: string): string {
  return `callix-dialer:${username.trim().toLowerCase()}`;
}

export function leaderKey(username: string): string {
  return `callix_dialer_${username}_leader`;
}

const TAB_ROLE_KEY = 'callix_dialer_tab_role';
const BUS_FALLBACK_SUFFIX = '_bus';

export function newTabId(): string {
  const cryptoApi = typeof globalThis.crypto !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && 'randomUUID' in cryptoApi) return cryptoApi.randomUUID().slice(0, 8);
  return Math.random().toString(36).slice(2, 10);
}

function defaultChannelFactory(name: string): ChannelLike | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(name);
  } catch {
    return null;
  }
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Canal entre as abas do mesmo usuário. Responde ao ping sozinho; o resto é entregue aos
 * ouvintes. Sem BroadcastChannel, usa a chave `callix_dialer_<user>_bus` + evento `storage`.
 */
export class DialerTabBus {
  readonly tabId: string;
  readonly username: string;
  private readonly channel: ChannelLike | null;
  private readonly storage: Storage | null;
  private readonly listeners = new Set<(message: TabMessage) => void>();
  private readonly status: () => TabStatus;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private closed = false;

  constructor(options: DialerTabBusOptions) {
    this.username = options.username;
    this.tabId = options.tabId ?? newTabId();
    this.status = options.status;
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    const factory = options.channelFactory ?? defaultChannelFactory;
    this.channel = factory(channelName(this.username));
    if (this.channel) {
      this.channel.onmessage = (event: { data: unknown }) => this.receive(event.data);
    } else if (typeof window !== 'undefined' && this.storage) {
      const key = this.fallbackKey();
      this.storageListener = (event: StorageEvent) => {
        if (event.key !== key || !event.newValue) return;
        try {
          const parsed = JSON.parse(event.newValue) as { message?: unknown };
          this.receive(parsed.message);
        } catch {
          // valor estranho na chave: ignora
        }
      };
      window.addEventListener('storage', this.storageListener);
    }
  }

  private fallbackKey(): string {
    return `callix_dialer_${this.username}${BUS_FALLBACK_SUFFIX}`;
  }

  on(listener: (message: TabMessage) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  post(message: OutgoingTabMessage): void {
    if (this.closed) return;
    const full = { ...message, from: this.tabId } as TabMessage;
    if (this.channel) {
      try {
        this.channel.postMessage(full);
      } catch {
        // canal fechado pelo navegador
      }
      return;
    }
    if (this.storage) {
      try {
        this.storage.setItem(this.fallbackKey(), JSON.stringify({ nonce: Math.random(), at: Date.now(), message: full }));
      } catch {
        // storage cheio ou indisponível
      }
    }
  }

  private receive(data: unknown): void {
    if (this.closed || !data || typeof data !== 'object') return;
    const message = data as TabMessage;
    if (typeof message.type !== 'string' || typeof message.from !== 'string' || message.from === this.tabId) return;
    if (message.type === 'ping') {
      const status = this.status();
      this.post({ type: 'pong', role: status.role, authenticated: status.authenticated });
    }
    for (const listener of this.listeners) listener(message);
  }

  /** pergunta quem está aí; devolve as abas que responderam dentro do prazo */
  probe(timeoutMs = PROBE_TIMEOUT_MS): Promise<PongInfo[]> {
    return new Promise((resolve) => {
      const found = new Map<string, PongInfo>();
      const off = this.on((message) => {
        if (message.type === 'pong') found.set(message.from, { tabId: message.from, role: message.role, authenticated: message.authenticated });
      });
      this.post({ type: 'ping' });
      setTimeout(() => {
        off();
        resolve([...found.values()]);
      }, timeoutMs);
    });
  }

  close(): void {
    this.closed = true;
    this.listeners.clear();
    if (this.channel) {
      this.channel.onmessage = null;
      try {
        this.channel.close();
      } catch {
        // já fechado
      }
    }
    if (this.storageListener && typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }
}

/** entre as abas que responderam, há uma líder (conexão ativa)? */
export function findLeader(pongs: PongInfo[]): PongInfo | null {
  return pongs.find((pong) => pong.role === 'leader' && pong.authenticated) ?? null;
}

/** alguma aba segue a decisão "usar o discador" (líder ou membro)? */
export function anyDialerTab(pongs: PongInfo[]): boolean {
  return pongs.some((pong) => pong.role !== null);
}

// ------------------------------------------------------------------ trava da líder

export interface LeaderLock {
  tabId: string;
  at: number;
}

export function readLeader(username: string, storage: Storage | null = defaultStorage(), now = Date.now()): LeaderLock | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(leaderKey(username));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LeaderLock>;
    if (typeof parsed.tabId !== 'string' || typeof parsed.at !== 'number') return null;
    if (now - parsed.at > LEADER_STALE_MS) return null;
    return { tabId: parsed.tabId, at: parsed.at };
  } catch {
    return null;
  }
}

/**
 * Tenta assumir a liderança. Falha se outra aba segura a trava e ela ainda vale.
 * Quem já é a dona renova o batimento.
 */
export function claimLeader(username: string, tabId: string, storage: Storage | null = defaultStorage(), now = Date.now()): boolean {
  if (!storage) return true;
  const current = readLeader(username, storage, now);
  if (current && current.tabId !== tabId) return false;
  try {
    storage.setItem(leaderKey(username), JSON.stringify({ tabId, at: now } satisfies LeaderLock));
  } catch {
    // sem storage a coordenação fica só pelo canal
  }
  return true;
}

export function releaseLeader(username: string, tabId: string, storage: Storage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    const raw = storage.getItem(leaderKey(username));
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<LeaderLock>;
    if (parsed.tabId === tabId) storage.removeItem(leaderKey(username));
  } catch {
    // ignora
  }
}

// ------------------------------------------------------------------ papel desta aba (sessionStorage)

export interface StoredTabRole {
  username: string;
  role: TabRole;
  at: number;
}

function defaultSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function saveTabRole(username: string, role: TabRole, storage: Storage | null = defaultSessionStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(TAB_ROLE_KEY, JSON.stringify({ username, role, at: Date.now() } satisfies StoredTabRole));
  } catch {
    // ignora
  }
}

export function readTabRole(username: string, storage: Storage | null = defaultSessionStorage()): TabRole | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(TAB_ROLE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTabRole>;
    if (parsed.username !== username) return null;
    return parsed.role === 'leader' || parsed.role === 'member' ? parsed.role : null;
  } catch {
    return null;
  }
}

export function clearTabRole(storage: Storage | null = defaultSessionStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(TAB_ROLE_KEY);
  } catch {
    // ignora
  }
}

/** prazo para um membro assumir depois do aviso `leaving` da líder (com sorteio) */
export function takeoverDelayMs(random = Math.random()): number {
  return TAKEOVER_GRACE_MS + Math.floor(random * TAKEOVER_JITTER_MS);
}
